import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IValidateSerialsDTO from "@models/DTO/IValidateSerialsDTO";
import IOnValidateSerialsDTO, { IValidateSerialResult } from "@models/DTO/IOnValidateSerialsDTO";
import "./BulkGenerateSerialsSidebar.scss";

interface GeneratedSerial {
    value: string;
    isValid: boolean | null; // null = pending validation
    error?: string;
}

interface AddProgress {
    processed: number;
    total: number;
}

type Props = {
    cancel: () => void;
    /**
     * Receives the valid serial values. Return a promise to keep the sidebar busy until it settles: Add stays
     * disabled (a second click is dropped) and a progress bar shows. Call `reportProgress` to advance it.
     */
    onAdd: (serials: string[], reportProgress: (processed: number, total: number) => void) => void | Promise<unknown>;
    maxQty?: number;
    embedded?: boolean;
};

export const BulkGenerateSerialsSidebar = ({ cancel, onAdd, maxQty, embedded }: Props) => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [prefix, setPrefix] = useState("");
    const [startNumber, setStartNumber] = useState<number>(1);
    const [endNumber, setEndNumber] = useState<number>(10);
    const [padWidth, setPadWidth] = useState<number>(3);

    const [generatedSerials, setGeneratedSerials] = useState<GeneratedSerial[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    // OR-1550: the ref is the click guard (set synchronously, before any await or re-render); the state drives the UI.
    const isAddingRef = useRef(false);
    const isMountedRef = useRef(true);
    const [isAdding, setIsAdding] = useState(false);
    const [progress, setProgress] = useState<AddProgress | null>(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const validCount = generatedSerials.filter((s) => s.isValid === true).length;
    const invalidCount = generatedSerials.filter((s) => s.isValid === false).length;
    const pendingCount = generatedSerials.filter((s) => s.isValid === null).length;
    const allValidated = generatedSerials.length > 0 && pendingCount === 0;
    const canAdd = allValidated && invalidCount === 0 && !isAdding;

    const validate = useCallback(
        (serials: GeneratedSerial[]) => {
            setGeneratedSerials(serials.map((serial) => ({ value: serial.value, isValid: null })));
            setIsValidating(true);

            const payload: IValidateSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                serials: serials.map((s) => s.value),
            };

            socket.emit(EndpointsEnum.SerialValidateSerials, payload);
        },
        [userModel.guid, userModel.token, supplierModel.guid, socket]
    );

    const generateSequence = useCallback(() => {
        if (startNumber > endNumber) return;

        const serials: GeneratedSerial[] = [];
        for (let i = startNumber; i <= endNumber; i++) {
            const paddedNum = String(i).padStart(padWidth, "0");
            serials.push({
                value: `${prefix}${paddedNum}`,
                isValid: null,
            });
        }

        validate(serials);
    }, [prefix, startNumber, endNumber, padWidth, validate]);

    const removeSerial = useCallback((index: number) => {
        setGeneratedSerials((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleAddAll = useCallback(async () => {
        if (isAddingRef.current) return;

        const validSerials = generatedSerials.filter((s) => s.isValid === true).map((s) => s.value);
        if (validSerials.length === 0) return;

        isAddingRef.current = true;
        setIsAdding(true);
        setProgress({ processed: 0, total: validSerials.length });

        try {
            await onAdd(validSerials, (processed, total) => {
                if (isMountedRef.current) setProgress({ processed, total });
            });
        } finally {
            isAddingRef.current = false;

            if (isMountedRef.current) {
                setIsAdding(false);
                setProgress(null);
                // Show what the add actually did: created values now read "Serial already exists" and must be
                // removed before Add is enabled again; values that failed for another reason can be retried.
                validate(generatedSerials);
            }
        }
    }, [generatedSerials, onAdd, validate]);

    // Listen for validation results
    useEffect(() => {
        const onValidateSerials = (data: IOnValidateSerialsDTO) => {
            setGeneratedSerials((prev) => {
                const resultMap = new Map<string, IValidateSerialResult>();
                for (const result of data.results) {
                    resultMap.set(result.serial, result);
                }

                const updated = prev.map((serial) => {
                    const result = resultMap.get(serial.value);
                    if (result) {
                        return {
                            ...serial,
                            isValid: result.isValid,
                            error: result.error,
                        };
                    }
                    return serial;
                });

                return updated;
            });
            setIsValidating(false);
        };

        socket.on(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);
        return () => {
            socket.off(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);
        };
    }, [socket]);

    const previewValue = `${prefix}${String(startNumber).padStart(padWidth, "0")} ... ${prefix}${String(endNumber).padStart(padWidth, "0")}`;
    const count = Math.max(0, endNumber - startNumber + 1);
    const exceedsMax = maxQty !== undefined && count > maxQty;
    const progressPercent = progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

    return (
        <div className={`BulkGenerateSerialsSidebar ${!embedded ? "p-2" : ""}`}>
            {!embedded && (
                <>
                    <div className="text-end">
                        <OwButton centerIconClass="fas fa-times" fn={cancel} className="btn-danger btn-sm" disabled={isAdding} />
                    </div>

                    <OwTitle title="bulk_generate_serials" />
                    <hr />
                </>
            )}

            {/* Sequence configuration */}
            <div className="mb-3">
                <OwLabel title="prefix" />
                <OwInputWrapper
                    name="prefix"
                    value={prefix}
                    fn={(e: any) => setPrefix(e.target.value)}
                    placeholder="SN-"
                    disabled={isAdding}
                />
            </div>

            <div className="row mb-3">
                <div className="col-6">
                    <OwLabel title="start" />
                    <OwInputWrapper
                        name="startNumber"
                        value={startNumber}
                        type="number"
                        fn={(e: any) => setStartNumber(Number(e.target.value))}
                        min={0}
                        disabled={isAdding}
                    />
                </div>
                <div className="col-6">
                    <OwLabel title="end" />
                    <OwInputWrapper
                        name="endNumber"
                        value={endNumber}
                        type="number"
                        fn={(e: any) => setEndNumber(Number(e.target.value))}
                        min={0}
                        disabled={isAdding}
                    />
                </div>
            </div>

            <div className="mb-3">
                <div className="d-flex align-items-center">
                    <OwLabel title="pad_width" noBottomMargin />
                    <OwIconTooltip
                        iconClassName="fas fa-info-circle text-muted ms-2"
                        tooltipContent="Number of digits to zero-pad. e.g. pad width 3: 1 → 001, 42 → 042"
                        placement="right"
                    />
                </div>
                <OwInputWrapper
                    name="padWidth"
                    value={padWidth}
                    type="number"
                    fn={(e: any) => setPadWidth(Number(e.target.value))}
                    min={1}
                    max={10}
                    disabled={isAdding}
                />
            </div>

            {/* Preview */}
            <div className="mb-3">
                <OwLabel title="preview" className="fw-bold" noBottomMargin />
                <div className="text-muted small">{previewValue}</div>
                <div className="text-muted small">
                    ({count} serials{maxQty !== undefined ? ` / ${maxQty} max` : ""})
                </div>
                {exceedsMax && (
                    <div className="text-danger small">
                        <OwLabel title="exceeds_max_quantity" skipTranslate noBottomMargin className="text-danger" />
                    </div>
                )}
            </div>

            <div className="mb-3">
                <OwButton
                    title="generate_and_validate"
                    fn={generateSequence}
                    disabled={count <= 0 || isValidating || exceedsMax || isAdding}
                    className="btn-primary btn-sm"
                />
            </div>

            <hr />

            {/* Generated serials list */}
            {generatedSerials.length > 0 && (
                <div className="generated-serials-section">
                    <div className="mb-2">
                        <OwLabel title="generated_serials" className="fw-bold" noBottomMargin />
                        <span className="text-muted ms-2 small">
                            ({validCount} valid, {invalidCount} invalid{pendingCount > 0 ? `, ${pendingCount} validating` : ""})
                        </span>
                    </div>

                    <div className="serial-list mb-3">
                        {generatedSerials.map((serial, index) => (
                            <div
                                key={index}
                                className={`serial-row d-flex align-items-center mb-1 ${serial.isValid === false ? "has-error" : ""}`}>
                                <span className="serial-status me-2">
                                    {serial.isValid === null && <i className="fas fa-spinner fa-spin text-secondary" />}
                                    {serial.isValid === true && <i className="fas fa-check text-success" />}
                                    {serial.isValid === false && <i className="fas fa-exclamation-triangle text-danger" />}
                                </span>
                                <span className="serial-value flex-grow-1">{serial.value}</span>
                                {serial.isValid === false && serial.error && <span className="text-danger small me-2">{serial.error}</span>}
                                <OwButton
                                    centerIconClass="fas fa-times"
                                    fn={() => removeSerial(index)}
                                    className="btn-outline-danger btn-sm"
                                    disabled={isAdding}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-end">
                        <OwButton
                            title="add_valid_serials"
                            fn={handleAddAll}
                            disabled={!canAdd}
                            active={isAdding}
                            disableOnActive={true}
                            className="btn-primary btn-sm me-2"
                        />
                        {!embedded && <OwButton title="cancel" fn={cancel} className="btn-danger btn-sm" disabled={isAdding} />}
                    </div>

                    {progress && (
                        <div className="mt-2" data-testid="bulk-serials-progress">
                            <div
                                className="progress"
                                role="progressbar"
                                aria-label={intl.formatMessage({ id: "adding_serials", defaultMessage: "Adding serials" })}
                                aria-valuenow={progress.processed}
                                aria-valuemin={0}
                                aria-valuemax={progress.total}>
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    style={{ width: `${Math.max(progressPercent, 5)}%` }}
                                />
                            </div>
                            <div className="text-muted small mt-1">
                                {intl.formatMessage(
                                    { id: "adding_serials_progress", defaultMessage: "Adding serials… {processed} of {total}" },
                                    { processed: progress.processed, total: progress.total }
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
